/**
 * Prepare a photo/screenshot for import: downscale it in the browser and hand
 * back base64 JPEG bytes. Downscaling before upload keeps the request small (no
 * multipart needed) and the model cheap — a 4000px camera photo carries nothing
 * a recipe parser needs over ~1568px, which is also the size vision models read
 * at.
 *
 * Decoding goes through `createImageBitmap` first, falling back to an `<img>`
 * element. The bitmap path is decisively more robust on iOS Safari — it decodes
 * off the main thread, applies EXIF orientation, and handles tall stitched
 * screenshots and large photos that make an `<img>` fire `onerror` (which is
 * what surfaced as "Kon de foto niet verwerken" on a perfectly ordinary Crisp
 * screenshot). Errors are no longer swallowed: the caller shows the reason.
 */

export interface CapturedImage {
  mediaType: string;
  data: string;
}

/** The longest edge we send. Above this a photo is bytes the model just downsamples anyway. */
const MAX_DIM = 1568;

interface Decoded {
  width: number;
  height: number;
  /** The decoded image, drawable with either drawImage overload. */
  source: CanvasImageSource;
  close: () => void;
}

async function loadImageElement(file: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('kan de afbeelding niet laden'));
      el.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function decode(file: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { width: bmp.width, height: bmp.height, source: bmp, close: () => bmp.close() };
    } catch {
      // Fall through to the <img> path — some formats decode one way and not the other.
    }
  }
  const img = await loadImageElement(file);
  return { width: img.naturalWidth, height: img.naturalHeight, source: img, close: () => {} };
}

/** JPEG bytes off a canvas → the base64 `CapturedImage` the API takes. */
function canvasToCaptured(canvas: HTMLCanvasElement): CapturedImage {
  const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
  const comma = dataUrl.indexOf(',');
  if (comma < 0 || comma === dataUrl.length - 1) throw new Error('kon de afbeelding niet opslaan');
  return { mediaType: 'image/jpeg', data: dataUrl.slice(comma + 1) };
}

export async function captureImage(file: Blob): Promise<CapturedImage> {
  const src = await decode(file);
  try {
    if (!src.width || !src.height) throw new Error('afbeelding heeft geen afmetingen');
    const scale = Math.min(1, MAX_DIM / Math.max(src.width, src.height));
    const w = Math.max(1, Math.round(src.width * scale));
    const h = Math.max(1, Math.round(src.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas niet beschikbaar');
    ctx.drawImage(src.source, 0, 0, w, h);

    return canvasToCaptured(canvas);
  } finally {
    src.close();
  }
}

export interface CropBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * Cut a region (normalized 0..1) out of an already-captured image — the finished-
 * dish photo the importer located inside a screenshot — and return it as its own
 * `CapturedImage`. Best-effort: throws on a decode/encode failure or a box too
 * small to be a real photo, and the caller keeps the uncropped picture.
 */
export async function cropCapturedImage(image: CapturedImage, box: CropBox): Promise<CapturedImage> {
  const bytes = Uint8Array.from(atob(image.data), (c) => c.charCodeAt(0));
  const src = await decode(new Blob([bytes], { type: image.mediaType }));
  try {
    if (!src.width || !src.height) throw new Error('afbeelding heeft geen afmetingen');
    const sx = clamp01(box.x) * src.width;
    const sy = clamp01(box.y) * src.height;
    const sw = clamp01(box.w) * src.width;
    const sh = clamp01(box.h) * src.height;
    // A degenerate or near-empty box is not a usable dish photo — bail and let the
    // full image stand rather than store a sliver.
    if (sw < src.width * 0.1 || sh < src.height * 0.05 || sw < 32 || sh < 32) {
      throw new Error('bijsnijden te klein');
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw);
    canvas.height = Math.round(sh);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas niet beschikbaar');
    ctx.drawImage(src.source, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    return canvasToCaptured(canvas);
  } finally {
    src.close();
  }
}

/** Up to six images (the import cap); a recipe spanning more screenshots is rare. */
export function captureImages(files: FileList | File[]): Promise<CapturedImage[]> {
  return Promise.all(Array.from(files).slice(0, 6).map(captureImage));
}

/** The base64 `CapturedImage` as a data URL, for drawing it into an `<img>`. */
export function capturedToDataUrl(image: CapturedImage): string {
  return `data:${image.mediaType};base64,${image.data}`;
}

/**
 * Load an already-stored image (a same-origin recipe-image path) back into a
 * `CapturedImage`, so it can be re-cropped. Used when re-cropping the picture of a
 * recipe that is already saved and whose original upload is no longer in memory.
 */
export async function captureImageFromUrl(url: string): Promise<CapturedImage> {
  const res = await fetch(url);
  if (!res.ok) throw new Error('kan de afbeelding niet laden');
  return captureImage(await res.blob());
}
